// @flow

import { type Axios } from 'axios'
import ClientOAuth2 from 'client-oauth2'
import Customer from '../CustomerArea/Customer'
import {
  type ConfigurationsPayload,
  type ConfigurationPayload,
  type ConfigurationReceiptPayload,
  saveShoppingCart,
  fetchShoppingCartByLotId,
  fetchConfigurations,
  fetchConfiguration,
  deleteConfiguration,
  configurationCustomerContact,
  fetchConfigurationReceipt,
  fetchConfigurationReceiptPdf,
  editProfilCustomer,
  editPasswordCustomer,
  fetchMe,
} from '../CustomerArea/api'
import ConfigurationShoppingCart from '../FlatConfigurator/MainMenu/Configuration/ShoppingCart/ConfigurationShoppingCart'
import { CustomerContextApi } from './CustomerContextApi'
import { attachCustomerToContext } from '../ErrorReporting'

type DispatchCustomerUpdateTrigger = (newCustomer: Customer) => Promise<void>
type ShowRegistrationModalTrigger = () => Promise<?Customer>
type ShowLoginModalTrigger = (cancelable: boolean) => Promise<?Customer>
type LogOutCustomerTrigger = () => Promise<void>

export default class ReactCustomerContextApi implements CustomerContextApi {
  +_httpClient: Axios
  +_authClient: ClientOAuth2
  +_dispatchCustomerUpdate: DispatchCustomerUpdateTrigger
  +_showRegistrationModal: ShowRegistrationModalTrigger
  +_showLoginModal: ShowLoginModalTrigger
  +_logOutCustomer: LogOutCustomerTrigger

  customer: ?Customer

  constructor(
    httpClient: Axios,
    authClient: ClientOAuth2,
    customerRefreshed: DispatchCustomerUpdateTrigger,
    showRegistrationModal: ShowRegistrationModalTrigger,
    showLoginModal: ShowLoginModalTrigger,
    logOutCustomer: LogOutCustomerTrigger,
    customer: ?Customer,
  ) {
    this._httpClient = httpClient
    this._authClient = authClient
    this._dispatchCustomerUpdate = customerRefreshed
    this._showRegistrationModal = showRegistrationModal
    this._showLoginModal = showLoginModal
    this._logOutCustomer = logOutCustomer

    this.customer = customer
  }

  replaceCustomer(newCustomer: ?Customer): CustomerContextApi {
    attachCustomerToContext(newCustomer)

    return new ReactCustomerContextApi(
      this._httpClient,
      this._authClient,
      this._dispatchCustomerUpdate,
      this._showRegistrationModal,
      this._showLoginModal,
      this._logOutCustomer,
      newCustomer,
    )
  }

  isAuthenticated(): boolean {
    return !!this.customer
  }

  async startRegistrationProcess(): Promise<?Customer> {
    this.customer = await this._showRegistrationModal()

    return this.customer
  }

  async startLoginProcess(cancelable: boolean = true): Promise<?Customer> {
    this.customer = await this._showLoginModal(cancelable)

    return this.customer
  }

  async logOutCustomer() {
    return this._logOutCustomer()
  }

  async saveShoppingCart(
    shoppingCart: ConfigurationShoppingCart,
  ): Promise<string> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const { customer, configurationId } = await saveShoppingCart(
      this._httpClient,
      this.customer,
      shoppingCart,
    )

    this._updateCustomerIfNeeded(customer)

    return configurationId
  }

  async fetchShoppingCartByLotId(
    lotId: string,
  ): Promise<?ConfigurationShoppingCart> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const {
      customer,
      configurationShoppingCart,
    } = await fetchShoppingCartByLotId(this._httpClient, this.customer, lotId)

    this._updateCustomerIfNeeded(customer)

    return configurationShoppingCart
  }

  async fetchConfigurations(): Promise<ConfigurationsPayload> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const { customer, configurationsPayload } = await fetchConfigurations(
      this._httpClient,
      this.customer,
    )

    this._updateCustomerIfNeeded(customer)

    return configurationsPayload
  }

  async fetchConfiguration(
    configurationId: string,
  ): Promise<ConfigurationPayload> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const { customer, configurationPayload } = await fetchConfiguration(
      this._httpClient,

      this.customer,

      configurationId,
    )

    this._updateCustomerIfNeeded(customer)

    return configurationPayload
  }

  async fetchConfigurationReceipt(
    configurationId: string,
  ): Promise<ConfigurationReceiptPayload> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const { customer, configurationReceipt } = await fetchConfigurationReceipt(
      this._httpClient,
      this.customer,
      configurationId,
    )

    this._updateCustomerIfNeeded(customer)

    return configurationReceipt
  }

  async fetchConfigurationReceiptPdf(receiptPdfUrl: string): Promise<string> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const {
      customer,
      configurationReceiptPdf,
    } = await fetchConfigurationReceiptPdf(
      this._httpClient,
      this.customer,
      receiptPdfUrl,
    )

    this._updateCustomerIfNeeded(customer)

    return configurationReceiptPdf
  }

  async deleteConfiguration(configurationId: string): Promise<void> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    const { customer } = await deleteConfiguration(
      this._httpClient,
      this.customer,
      configurationId,
    )

    this._updateCustomerIfNeeded(customer)
  }

  async _updateCustomerIfNeeded(newCustomer: Customer) {
    if (this.customer !== newCustomer) {
      await this._dispatchCustomerUpdate(newCustomer)
    }
  }

  async refreshCustomer(): Promise<Customer> {
    const customer = this.customer
    if (!customer) {
      throw new Error('No customer logged in')
    }

    const me = await fetchMe(this._httpClient, customer.authToken)

    const refreshedCustomer = customer.refreshCustomer(me.me)

    this._updateCustomerIfNeeded(refreshedCustomer)

    return refreshedCustomer
  }

  async configurationCustomerContact(
    configurationId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ): Promise<Array<{| field: ?string, message: string |}>> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    return configurationCustomerContact(
      this._httpClient,
      this.customer,
      configurationId,
      firstName,
      lastName,
      email,
      phone,
    )
  }

  async editProfilCustomer(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ): Promise<Array<{| field: ?string, message: string |}>> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    return editProfilCustomer(
      this._httpClient,
      this.customer,
      firstName,
      lastName,
      email,
      phone,
    )
  }

  async editPasswordCustomer(
    oldPassword: string,
    newPassword: string,
  ): Promise<Array<{| field: ?string, message: string |}>> {
    if (!this.customer) {
      throw new Error('No customer logged in')
    }

    return editPasswordCustomer(
      this._httpClient,
      this.customer,
      oldPassword,
      newPassword,
    )
  }
}
